import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { I18nService } from '../../services/i18n.service';
import { calculateSize, sanitizeUnicodeText } from '../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';

@Component({
  selector: 'app-sensor-input',
  imports: [FormsModule, NgClass, NzIconModule],
  templateUrl: './sensor-input.html',
  styleUrls: ['./sensor-input.scss'],
})
export class SensorInputComponent {
  calculateSize = calculateSize;
  @ViewChild('sensorNameInput') sensorNameInput!: ElementRef<HTMLInputElement>;

  public readonly i18n = inject(I18nService);

  /** ===== Signal Inputs / Outputs ===== */
  /** Two-way binding support from parent: [(sensorName)]="..." */
  sensorName = input<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  sensorNameChange = output<string>();

  /** Additional inputs */
  prefix = input<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  showError = input<boolean>(false);
  showLabel = input<boolean>(true);

  /** ===== Local state (signals) ===== */
  /** Local editable value mirroring the input() from parent */
  name = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);

  /** UI flags (match original behavior) */
  displayValidation = signal<boolean>(false);
  greyFlag = signal<boolean>(false);
  showBorder = signal<boolean>(false);
  showAlert = signal<boolean>(false);
  buttonDisabled = signal<boolean>(false);

  /** ===== Derived (computed) ===== */
  hasValue = computed(() => {
    const v = this.name();
    return v !== GLOBAL_CONSTANT.EMPTY_STRING && v !== null && v !== undefined;
  });

  containsSlash = computed(() => (this.name() ?? GLOBAL_CONSTANT.EMPTY_STRING).includes('/'));

  /** Combined error (external OR local slash rule) */
  isInvalid = computed(() => this.showError() || this.containsSlash());

  constructor() {
    // Keep local name in sync when parent updates sensorName input
    effect(() => {
      const incoming = this.sensorName();
      // Only update local name if parent changed it (avoid overwriting in-mid edit)
      this.name.set(incoming ?? GLOBAL_CONSTANT.EMPTY_STRING);
    });
  }

  /** ===== Public API / Handlers ===== */

  refocus(): void {
    this.sensorNameInput?.nativeElement?.focus();
  }

  /** Keyboard entry validation (called e.g. on keyup) */
  keyPressAlphaNumeric(_event: any): void {
    if (this.hasValue()) {
      if (this.containsSlash()) {
        this.displayValidation.set(true);
        this.greyFlag.set(false);
        this.showAlert.set(true);
        this.showBorder.set(true);
      } else {
        this.displayValidation.set(true);
        this.showBorder.set(false);
        this.showAlert.set(false);
        this.greyFlag.set(true);
      }
    } else {
      this.showBorder.set(false);
      this.showAlert.set(false);
      this.greyFlag.set(true);
      this.displayValidation.set(true);
    }

    // Emit to parent whenever edited
    this.sensorNameChange.emit(this.name());
  }

  /** Blur validation (matches original logic) */
  onInputFocusOut(): void {
    if (this.hasValue()) {
      if (this.containsSlash()) {
        this.displayValidation.set(true);
        this.greyFlag.set(false);
        this.showAlert.set(true);
        this.showBorder.set(true);
        this.buttonDisabled.set(true);
      } else {
        this.displayValidation.set(false);
        this.showBorder.set(false);
        this.showAlert.set(false);
        this.greyFlag.set(false);
        this.buttonDisabled.set(false);
      }
    } else {
      this.displayValidation.set(false);
      this.buttonDisabled.set(false);
      this.greyFlag.set(false);
      this.showAlert.set(false);
      this.showBorder.set(false);
    }

    // Emit to parent on blur as well
    this.sensorNameChange.emit(this.name());
  }

  /** Write side for the input box (one-way down + one-way up pattern) */
  onInputChange(value: string): void {
    this.name.set(value ?? GLOBAL_CONSTANT.EMPTY_STRING);
    // Optionally emit on each change:
    this.sensorNameChange.emit(this.name());
  }

  onTextInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const cleaned = sanitizeUnicodeText(inputEl.value);

    if (cleaned !== inputEl.value) {
      inputEl.value = cleaned; // update the DOM value
    }
    //sync to model:
    this.onInputChange(cleaned);
  }
}
